// @owners { team: patients-team }
import { Experimentation } from '@alto/experimentation';
import { ContactUs } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import { Link, SimpleContainer, SimpleContainerHeader, Text } from '@alto/pocky';
import React from 'react';
import { CONTACT_US_ORIGIN, EXPENSIVE_THRESHOLD_3000, EXPENSIVE_THRESHOLD_500 } from '~shared/constants';
import BusinessHours from './BusinessHours';
import CollapsibleSection from './CollapsibleSection';
import Breadcrumbs from '~web/components/Breadcrumbs';
import { ContentWrapper, FullFlexRow, Main, PageBackground, Sidebar } from '~web/components/Page';
import PageHeader from '~web/components/PageHeader';
import { phoneSupport } from '~web/constants';

const FAQs = React.memo(function MemoFaqs({ useNewThreshold }: { useNewThreshold: boolean }) {
  let expensiveThreshold = EXPENSIVE_THRESHOLD_500;

  if (useNewThreshold) {
    expensiveThreshold = EXPENSIVE_THRESHOLD_3000;
  }

  return (
    <>
      <CollapsibleSection
        title="What are your hours of operation? Where do you deliver?"
        content={
          <div>
            <Text>
              Alto’s hours of operation vary by location. View the full details for each pharmacy at{' '}
              <Link
                href="https://alto.com/locations"
                target="_blank"
                rel="noopener noreferrer"
              >
                alto.com/locations
              </Link>
              .
            </Text>
            <br />
            <Text>
              Have a question? Our team is available Monday - Friday 6 am - 9 pm PT and Saturday - Sunday 7 am - 6 pm PT
              at <Link href={`tel:${phoneSupport}`}>{phoneSupport}</Link> or by secure message in this app.
            </Text>
          </div>
        }
      />
      <CollapsibleSection
        title="Do I need to be home for my delivery?"
        content={
          <div>
            <Text>
              No, in most instances, you can designate a safe place for medications to be left if you will not be home
              to receive the delivery directly. For controlled substances and medications more than $
              {expensiveThreshold}, your signature will be required, or you can designate an authorized representative
              to sign in your place.
            </Text>
            <br />
            <Text>
              You can add special delivery instructions by going to the "Delivery Addresses" section of your Account
              settings in this app.
            </Text>
          </div>
        }
      />
      <CollapsibleSection
        title="What insurance do you accept?"
        content={
          <Text>
            We accept most major insurance plans, including government-administered plans, and always work to get you
            the lowest price. Please note that coverage varies by state. For up to date information on coverage in your
            area call us at <Link href={`tel:${phoneSupport}`}>{phoneSupport}</Link> or send a secure message in this
            app.
          </Text>
        }
      />
      <CollapsibleSection
        title="My doctor sent you a prescription, and I just created an account, but I don’t see the prescription info in this app. What’s the deal?"
        content={
          <Text>
            An Alto account is automatically created for you when your doctor sends us your first prescription. If you
            created your own account prior to when the doctor sent your prescription, you may have a duplicate account
            in our system. Please give us a call at <Link href={`tel:${phoneSupport}`}>{phoneSupport}</Link>.
          </Text>
        }
      />
      <CollapsibleSection
        title="How will I know when to expect my delivery?"
        content={
          <Text>
            We’ll text or call you when your prescription is ready for delivery; just select the delivery date and
            window that works best for you. Your medications will be hand-delivered by a member of our local courier
            team, and we’ll keep you updated throughout the delivery process via text.
          </Text>
        }
      />
      <CollapsibleSection
        title="Why does it say I can’t schedule my delivery until a later date?"
        content={
          <Text>
            Your prescription may not be able to be processed through insurance until that date or the doctor may have
            dated the prescription for use only after that date. Call us at{' '}
            <Link href={`tel:${phoneSupport}`}>{phoneSupport}</Link> or send us a message in this app if you have any
            questions.
          </Text>
        }
      />
      <CollapsibleSection
        title="I scheduled my delivery but I don’t know what my copay is. When will I know how much it costs?"
        content={
          <Text>
            That means we’re still working to get you the best price on your medications. Once we’ve processed your
            insurance and applied all available coupons, we’ll reach out to confirm your copay before the delivery
            leaves our pharmacy.
          </Text>
        }
      />
      <CollapsibleSection
        title="Will you ever charge me without confirming the copay?"
        content={
          <Text>
            We will confirm your first copay for each prescription and any copays over $30 that are not available to you
            when scheduling a delivery. We will also reach out if your copay changes between auto refill deliveries.
          </Text>
        }
      />
      <CollapsibleSection
        title="How do I add, update or delete my payment options?"
        content={
          <Text>
            You can manage your payment options by going to the "Payment Methods" section of your Account settings in
            this app.
          </Text>
        }
      />
      <CollapsibleSection
        title="Can I use my FSA or HSA with Alto?"
        content={
          <Text>
            Yes, you can use FSA and HSA funds at Alto. You can manage your payment options by going to the "Payment
            Methods" section of your Account settings in this app.
          </Text>
        }
      />
      <CollapsibleSection
        title="Should I tip my courier?"
        content={
          <Text>
            There’s no need to tip your courier, but we always appreciate hearing feedback on a job well done. Share
            your feedback by sending us a message in this app.
          </Text>
        }
      />
      <CollapsibleSection
        title="Can I request a transfer using the app?"
        content={
          <div>
            <Text>
              Yes, you can start a transfer request by heading to "Add Medications" on the Meds page or the "Send
              Prescriptions to Alto" section of your Account settings. We’ll coordinate the transfer and search for any
              savings on your medications. Because the transfer relies on your current pharmacy to provide information,
              it may take up to a few business days for the transfers to complete. When it’s ready, we’ll reach out to
              schedule your delivery.
            </Text>
            <br />
            <Text>
              Note: The prescription must have refills remaining on it to be eligible for transfer. Rules regarding the
              transfer of prescriptions for controlled substances vary by state.
            </Text>
          </div>
        }
      />
      <CollapsibleSection
        title="What is auto refill and how does it work?"
        content={
          <Text>
            Auto refill makes your life easier by automatically sending your medication a few days before you run out.
            Manage your refill settings on the "Auto refills" section of your Account settings. We’ll notify you before
            each delivery to confirm details and price. When your prescription runs out of refills, we’ll automatically
            request a renewal from your doctor. Some medications, such as controlled substances, are not eligible for
            auto refill.
          </Text>
        }
      />
      <CollapsibleSection
        title="Can I manage my family’s medications from my account?"
        content={
          <Text>
            Yes, you can manage your family’s prescriptions, all in one place! You can schedule deliveries, order
            refills, and update insurance on their behalf.
          </Text>
        }
      />
      <CollapsibleSection
        title="How do I add a family member to my account? How do I remove a family member from my account?"
        content={
          <Text>
            Please{' '}
            <ContactUs
              type="link"
              origin={CONTACT_US_ORIGIN.ProfileSupport}
              method="message"
            />{' '}
            that includes their full name, date of birth, and your relationship to that person. Please note that any
            family member 18 years or older will need to provide oral or written authorization before we can add or
            remove them to your account.
          </Text>
        }
      />
      <CollapsibleSection
        title="How do I archive prescriptions I’m not currently using?"
        content={
          <Text>
            Archiving prescriptions reduces clutter on your Meds list by hiding the prescriptions you’re not currently
            taking. Go to the Meds page of this app and select the prescription you’d like to archive. From the
            "Medication Details" view, select "Archive this prescription." The prescription will now be in your
            "Inactive" list, and you can un-archive the prescription at any time.
          </Text>
        }
      />
      <CollapsibleSection
        title="Why do you need my photo ID?"
        content={
          <Text>
            Per state and federal regulations, we are required to verify your identity when filling prescriptions for
            controlled substances.
          </Text>
        }
      />
      <CollapsibleSection
        title="Is the app secure?"
        content={
          <Text>
            Yes, we take security very seriously at Alto. We encrypt our user data and are fully HIPAA-compliant to
            ensure your privacy.
          </Text>
        }
      />
    </>
  );
});

const BREADCRUMBS = [
  {
    text: 'Account',
    to: '/me',
  },
  {
    text: 'FAQs',
  },
];

export const ProfileSupport = () => {
  const { value: useNewThreshold } = Experimentation.useFeatureFlag('use_new_expensive_threshold');

  return (
    <PageBackground>
      <ContentWrapper>
        <FullFlexRow>
          <Breadcrumbs breadcrumbs={BREADCRUMBS} />
          <PageHeader>FAQs</PageHeader>
        </FullFlexRow>
        <Main>
          <SimpleContainer>
            <FAQs useNewThreshold={useNewThreshold} />
          </SimpleContainer>
        </Main>
        <Sidebar>
          <SimpleContainer>
            <SimpleContainerHeader>Business Hours</SimpleContainerHeader>
            <BusinessHours />
          </SimpleContainer>
        </Sidebar>
      </ContentWrapper>
    </PageBackground>
  );
};
